import type {Ref} from 'vue'
import {
    B2B_AUTH_EVENT,
    CHANGE_LOCALE_EVENT,
    CHANGE_PRICE_EVENT,
    EventBus,
    RESET_BOOKING_DATA,
    SEARCH_EVENT,
} from '@/utils/event-bus'
import {appInstance} from '@/utils/app-accessor'

import {clean} from '@/utils/helpers'
import {
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
} from '~src/utils/visa/visa-blank-states'
import {persistentStore, visaStore} from '@/composables/store-accessor'
import {useProductRuntimeStore} from '~/composables/product-runtime-store'
import {onmessage} from '~/utils/worker-helpers'
import {PRODUCT_NAME} from '~/utils/visa/visa-const'
import {newSearch as newSearchFn, search as searchFn} from '~/utils/store-helpers'

export const useVisaRuntimeStore = defineStore('visaRuntime', () => {
    const productRuntimeStore = useProductRuntimeStore()
    const {
        searchActiveCount,
        SET_OFFERS,
        START_SEARCH,
        STOP_SEARCH,
        stopSearch,
        STOP_FILTER,
        filterActive,
        SET_PARTIAL_SEARCH_RESULTS_CTS,
    } = productRuntimeStore

    const location: Ref = ref({})

    function RESET() {
        searchActiveCount.value = 0
    }

    async function _loadCountry(point: any, id: any) {
        try {
            const {
                countries: [country],
            } = await appInstance.$api.locations.get({id})
            return {[point]: country}
        } catch (e) {
            return {[point]: {}}
        }
    }

    async function loadLocation(id: any) {
        location.value = ((await _loadCountry('location', id)) as any).location
    }

    function SET_DESTINATION_COUNTRY(country: any) {
        location.value = country
    }

    function clientInit() {
        EventBus.$on('RESET', reset)
        EventBus.$on(RESET_BOOKING_DATA, reset)
        EventBus.$on(B2B_AUTH_EVENT, reset)
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        EventBus.$on(CHANGE_PRICE_EVENT, changePrice)
        onmessage(PRODUCT_NAME, STOP_FILTER, filterActive, SET_OFFERS)
    }

    async function changePrice({offerKey, prepareBookResponse}: any) {
        visaStore.REFRESH_BASKET_PRICE({offerKey, prepareBookResponse})
        persistentStore.REFRESH_CONDITIONS({offerKey, prepareBookResponse})
    }

    async function reload() {
        if (location.value.id) await loadLocation(location.value.id)
    }

    function reset() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), visaStore)
        RESET()
        visaStore.RESET()
    }

    function newSearch() {
        newSearchFn(stopSearch, SET_OFFERS, searchRequestBlank(), searchResponseBlank(), visaStore)
    }

    async function search(rq: any) {
        START_SEARCH()
        newSearchFn(stopSearch, SET_OFFERS, rq, searchResponseBlank(), visaStore)
        EventBus.$emit(SEARCH_EVENT)
        await searchFn(
            SET_PARTIAL_SEARCH_RESULTS_CTS,
            rq,
            PRODUCT_NAME,
            visaStore,
            appInstance.$api.searchVisa,
            'products',
            ['price']
        )
        STOP_SEARCH()
    }

    function load(offers = []) {
        appInstance.$workers.visaWorker.postMessage({action: 'load', offers})
    }

    const searchPageLink = computed(() => {
        return (searchRequest: any) => {
            const {...query} = searchRequest
            return {name: 'visaSearch', query}
        }
    })

    const searchRequestFromQuery = computed(() => {
        return (query: any) => {
            //TODO Need filter $route.query params
            const rq = Object.assign(searchRequestBlank(), query)
            if (rq.applicantCountryId) {
                rq.applicantCountryId = parseInt(rq.applicantCountryId, 10)
            }
            rq.convertToCurrency = persistentStore.currency
            if (rq.countryId) rq.countryId = parseInt(rq.countryId, 10)
            rq.adults = parseInt(rq.adults, 10)
            if (typeof rq.childrenAges === 'string') {
                rq.childrenAges = [parseInt(rq.childrenAges, 10)]
            } else {
                rq.childrenAges = rq.childrenAges.map(age => parseInt(age, 10))
            }
            clean(rq)
            return rq
        }
    })

    return {
        ...productRuntimeStore,
        location,
        loadLocation,
        SET_DESTINATION_COUNTRY,
        clientInit,
        changePrice,
        reset,
        reload,
        search,
        newSearch,
        load,
        searchPageLink,
        searchRequestFromQuery,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useVisaRuntimeStore, import.meta.hot))
